import { NgClass, AsyncPipe } from '@angular/common';
import { Component, computed, inject, Input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { UserPermissionsDirective } from '@bn/auth/directives';
import { AuthService, UserDetailsStateService } from '@bn/auth/services';
import { AccountStore, UserStore } from '@bn/auth/stores';
import { AppRoutes } from '@bn/dashboard-core/enums';
import { MainMenuComponent } from '@bn/layout/main-menu/main-menu.component';
import { UserProfileComponent } from '@bn/layout/user-profile/user-profile.component';
import { ClickActionType } from '@bn/shared/entities/models';
import { VersionCheckService } from '@bn/shared/services';
import { LazyLoadDirective } from '@bn/web-ui';
import { SearchComponent } from '../search/search.component';
import { DropdownMenuComponent } from '@bn/web-ui/dropdown-menu/dropdown-menu.component';
import { ButtonComponent } from '@bn/web-ui/button/button.component';
import { AddMenuComponent } from '../add-menu/add-menu.component';
import { addMenuConfig } from '../add-menu/add-menu.config';
import { BillingOverviewComponent } from '../billing-overview/billing-overview.component';
import { DashboardMenuSection, mainMenuChildRoutes, mainMenuConfig } from '../main-menu/main-menu.config';
import { NotificationsComponent } from '../notifications/notifications.component';
import { SupportComponent } from '../support/support.component';
import { buildUserProfileMenuItems } from '../user-profile/user-profile.config';

@Component({
  selector: 'bn-dashboard-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  imports: [
    NgClass,
    RouterLink,
    LazyLoadDirective,
    NotificationsComponent,
    UserProfileComponent,
    FaIconComponent,
    SearchComponent,
    MainMenuComponent,
    UserPermissionsDirective,
    DropdownMenuComponent,
    ButtonComponent,
    AddMenuComponent,
    BillingOverviewComponent,
    SupportComponent,
    AsyncPipe,
  ],
})
export class SidebarComponent {
  readonly authService = inject(AuthService);
  readonly versionCheckService = inject(VersionCheckService);
  readonly userStore = inject(UserStore);
  readonly #accountStore = inject(AccountStore);
  readonly billingBalance = computed(() => this.#accountStore.billingOverview()?.Balance);
  readonly accountRoute = `/${AppRoutes.Account}`;
  readonly billingRoute = `/${AppRoutes.Account}/${AppRoutes.Billing}`;
  readonly #userDetails = toSignal(inject(UserDetailsStateService).getUserDetails());
  readonly userProfileMenuItems = computed(() => buildUserProfileMenuItems(this.#userDetails()));
  readonly userEmail = computed(() => this.#userDetails()?.Email ?? '');
  readonly showBilling = computed(() => {
    const roles = this.#userDetails()?.Roles ?? [];

    if (this.userStore.hidePricing()) return false;

    return roles.includes('User') || roles.includes('SubuserBilling');
  });
  readonly userFullName = computed(() => {
    const u = this.#userDetails();

    return u ? `${u.FirstName ?? ''} ${u.LastName ?? ''}`.trim() : '';
  });

  readonly mainMenuConfig = mainMenuConfig;
  readonly mainMenuChildRoutes = mainMenuChildRoutes;
  readonly defaultOpenSections: string[] = [DashboardMenuSection.Delivery, DashboardMenuSection.EdgePlatform];
  readonly homeRoute = AppRoutes.Overview;

  @Input()
  public isMobileMenuOpened = false;

  public readonly toggleMenu = output<boolean>();

  public isDropdownOpened = false;
  public readonly addDropdownLinks = addMenuConfig;

  public onDropDownStateChanged(isOpened: boolean): void {
    this.isDropdownOpened = isOpened;
  }

  public onMenuItemClick(action: ClickActionType): void {
    if (action === 'toggle-menu') {
      this.onToggleMenu();
    }
  }

  public onLogout(): void {
    this.authService.logout();
  }

  private onToggleMenu(): void {
    this.toggleMenu.emit(!this.isMobileMenuOpened);
  }
}
